import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import FilterBar from '@/components/feature/FilterBar';
import Pagination from '@/components/base/Pagination';
import MovieCard from '@/components/base/MovieCard';
import { useSeo } from '@/hooks/useSeo';
import { applyFilters, getHotMovies, searchMovies, type FilterOptions } from '@/utils/movies';

const PAGE_SIZE = 8;

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const [input, setInput] = useState(query);
  const [filters, setFilters] = useState<FilterOptions>({ sort: 'hot' });
  const [page, setPage] = useState(1);

  useSeo({
    title: query ? `搜索“${query}”的结果 - 6080yy电影在线看` : '搜索影片 - 6080yy电影在线看',
    description: `在 6080yy电影在线看搜索“${query || '电影'}”，快速找到相关的高清电影资源，支持按地区、年代、类型筛选与排序。`,
    keywords: `${query || '电影搜索'},电影搜索,6080yy电影在线看,高清电影`,
  });

  useEffect(() => {
    setInput(query);
    setPage(1);
  }, [query]);

  const results = useMemo(() => applyFilters(searchMovies(query), filters), [query, filters]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentItems = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const suggestions = getHotMovies(6);

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = input.trim();
    setSearchParams(value ? { q: value } : {});
    setPage(1);
  };

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8">
      <nav className="flex items-center gap-1.5 text-sm text-foreground-500 mb-5 flex-wrap">
        <Link to="/" className="hover:text-accent-600 transition-colors cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center text-foreground-300"></i>
        <span className="text-foreground-900">搜索结果</span>
      </nav>

      <div className="max-w-2xl">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950 mb-4">
          影片搜索
        </h1>
        <form onSubmit={handleSearch} className="flex items-center gap-2 bg-background-50 rounded-full pl-5 pr-1.5 py-1.5 border border-background-300 focus-within:border-accent-400 transition-colors">
          <i className="ri-search-line text-foreground-400 text-lg w-5 h-5 flex items-center justify-center"></i>
          <input
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="输入电影名称、导演或演员关键词"
            className="flex-1 bg-transparent text-sm md:text-base text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
          />
          <button
            type="submit"
            className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </form>
      </div>

      {query ? (
        <>
          <div className="mt-6 mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-foreground-600">
            <span>关键词</span>
            <span className="px-2 py-0.5 rounded bg-accent-100 text-accent-900 font-label">“{query}”</span>
            <span>
              共找到 <span className="text-accent-600 font-heading font-bold">{results.length}</span> 部影片
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
            <aside className="lg:sticky lg:top-20 lg:self-start">
              <FilterBar
                value={filters}
                onChange={(next) => {
                  setFilters(next);
                  setPage(1);
                }}
              />
            </aside>

            <section>
              {currentItems.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
                    {currentItems.map((movie) => (
                      <MovieCard key={movie.id} movie={movie} />
                    ))}
                  </div>
                  <Pagination current={page} total={totalPages} onChange={setPage} />
                </>
              ) : (
                <div className="py-12 text-center bg-background-50 rounded-lg border border-background-200">
                  <i className="ri-search-eye-line text-5xl text-foreground-300 w-14 h-14 flex items-center justify-center mx-auto"></i>
                  <h2 className="mt-4 font-heading font-bold text-lg text-foreground-950">没有找到相关影片</h2>
                  <p className="mt-2 text-sm text-foreground-500 px-4">
                    换个关键词试试，或看看下面这些热门推荐。
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setSearchParams({});
                      setInput('');
                      setFilters({ sort: 'hot' });
                    }}
                    className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-label font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line w-4 h-4 flex items-center justify-center"></i>
                    清空搜索条件
                  </button>
                  <div className="mt-10 px-4 md:px-6">
                    <p className="text-sm font-label text-foreground-500 mb-4">热门推荐</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 text-left">
                      {suggestions.map((movie) => (
                        <MovieCard key={movie.id} movie={movie} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </section>
          </div>
        </>
      ) : (
        <div className="mt-10">
          <p className="text-sm font-label text-foreground-500 mb-4">大家都在搜</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {suggestions.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-2">
            {['科幻', '动作', '悬疑', '爱情', '动画', '喜剧'].map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => setSearchParams({ q: word })}
                className="px-4 py-1.5 rounded-full bg-background-100 text-foreground-600 text-sm hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}